import { useEffect, useRef, useState } from 'react'
import { getBlattPdf, type PortalSessionId } from '../../api/portal'
import type { BlattId } from '../../lib/teilnehmerakte'

export type SheetPdfState = { kind: 'loading' } | { kind: 'ready'; blob: Blob } | { kind: 'failed' }

/** Signing must show saved answers; retry rechecks saves before fetching the canonical PDF. */
export function useSheetPdf(
  blatt: BlattId | null,
  signed: boolean,
  sessionId: PortalSessionId,
  flush: (retryFailed?: boolean) => Promise<boolean>,
  language: string,
): { state: SheetPdfState | null; retry: () => void } {
  const [state, setState] = useState<SheetPdfState | null>(null)
  const [attempt, setAttempt] = useState(0)
  const retryRequested = useRef(false)
  const flushRef = useRef(flush)
  flushRef.current = flush

  useEffect(() => {
    const retryFailed = retryRequested.current
    retryRequested.current = false
    if (!blatt) {
      setState(null)
      return
    }
    let cancelled = false
    setState({ kind: 'loading' })
    void (async () => {
      try {
        if (!(await flushRef.current(retryFailed))) {
          if (!cancelled) setState({ kind: 'failed' })
          return
        }
        if (cancelled) return
        const blob = await getBlattPdf(blatt, sessionId)
        if (!cancelled) setState({ kind: 'ready', blob })
      } catch {
        if (!cancelled) setState({ kind: 'failed' })
      }
    })()
    return () => {
      cancelled = true
    }
    // `language` triggers a reread; the server uses the persisted session language.
  }, [blatt, signed, sessionId, language, attempt])

  return { state, retry: () => {
    retryRequested.current = true
    setState({ kind: 'loading' })
    setAttempt((value) => value + 1)
  } }
}
